import React, { useEffect, useMemo } from 'react';
import { RootDispatch, RootState } from 'store/types';
import * as testResults from 'store/testResults/selectors';
import { connect } from 'react-redux';
import {
  LabHistoricalResult,
  LabHistoricalComponent,
  LabResultsListItems,
  TestResultCategory
} from 'store/testResults/types';
import { oc } from 'ts-optchain';
import { useLocation } from 'react-router-dom';
import Divider from 'components/UI/Divider';
import {
  MuiBox,
  MuiContainer,
  MuiPaper,
  MuiTable,
  MuiTableBody,
  MuiTableHead,
  MuiTablePagination,
  MuiTableRow
} from 'theme/material-ui';
import { Container, TableCell, AnalyteTitle } from '../styled';

import Banner from 'components/UI/Banner/Banner';
import AbsoluteSpinner from 'components/common/AbsoluteSpinner/AbsoluteSpinner';
import { resetTestResultDetails } from 'store/testResults/actions';
import { getLabHistoricalCdr, getLabHistoricalMillennium } from 'store/testResults/lab/actions';
import { getLabValue, orNA } from './utils';
import { formatData } from './LabTrendLine/utils';
import MedHistoryBanner from 'screens/MedicalHistory/Banner';
import { Display, LabDataSource } from './LabTrendLine/enums';
import { LabTrendLineChart } from 'screens/TestResults/Lab/LabTrendLine/LabTrendLineChart';
import dayjs from 'dayjs';
import { headingFontSize, largeFontSize, Spacing } from 'modules/styles/variables';
import { AdapterSource } from 'modules/types/common';
import Typography from 'components/UI/Typography';
import { getImageByCategory } from 'store/testResults/utils';
import Svg from 'components/UI/Svg/Svg';

export interface Props {
  milleniumError: null | Error;
  milleniumLoading: boolean;
  cdrError: null | Error;
  cdrLoading: boolean;
  milleniumData: LabHistoricalResult[] | null;
  cdrData: LabHistoricalResult[] | null;
  dispatch: RootDispatch;
}

const LabHistoricalScreen = (props: Props) => {
  const {
    milleniumLoading,
    cdrLoading,
    milleniumError,
    cdrError,
    milleniumData,
    cdrData,
    dispatch
  } = props;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(12);

  // Lab info
  const state = oc(useLocation()).state({});
  const resultDetail: LabResultsListItems = oc(state).resultDetail({});

  const labName = oc(resultDetail).name('');
  const loincCodes = oc(resultDetail).loincCode('');
  const panelName = oc(resultDetail).panelName('');
  const unit = oc(resultDetail).valueQuantity.unit('');
  const dataSource = oc(resultDetail).source(LabDataSource.CDR);
  const displayType = dataSource === LabDataSource.CDR ? Display.TABLE : Display.CHART;
  const adapterSource = oc(resultDetail).adapterSource(AdapterSource.UTAH);

  const data = displayType === Display.TABLE ? cdrData : milleniumData;
  const formattedData = useMemo(() => formatData(data), [data]);

  useEffect(() => {
    return () => {
      dispatch(resetTestResultDetails());
    };
  }, []);

  useEffect(() => {
    if (displayType === Display.TABLE) {
      const cdrParams = {
        labName,
        panelName,
        unit
      };

      dispatch(getLabHistoricalCdr(cdrParams, adapterSource));
    } else {
      const millenniumParams = {
        labName,
        loincCodes,
        unit
      };

      dispatch(getLabHistoricalMillennium(millenniumParams, adapterSource));
    }
  }, [getLabHistoricalMillennium, getLabHistoricalCdr, displayType]);

  const handleChangePage = (event: React.FormEvent<HTMLInputElement>, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.FormEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (milleniumLoading || cdrLoading) {
    return <AbsoluteSpinner />;
  }

  if (
    (milleniumError && dataSource === LabDataSource.MILLENNIUM) ||
    (cdrError && dataSource === LabDataSource.CDR)
  ) {
    return (
      <MuiBox flex={1} alignItems="center" justifyContent="center" mt={Spacing.xLarge}>
        <Typography fontSize={largeFontSize} lineHeight={headingFontSize} textAlign="center">
          There was an issue fetching labs historical information
        </Typography>
      </MuiBox>
    );
  }
  const hasValues = formattedData[0]?.valueQuantity;
  const renderChart = displayType === Display.CHART && formattedData.length >= 3 && hasValues;

  return (
    <>
      <Banner message={panelName} />
      <MedHistoryBanner />
      <Divider />
      {renderChart ? (
        <MuiContainer style={{ paddingBottom: Spacing.none }} maxWidth="xl">
          <MuiBox
            component={MuiPaper}
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={2}
            mt={3}
            elevation={0}
          >
            <MuiBox data-testid="lab-trendline-header" display="flex" flexDirection="column">
              <AnalyteTitle>{labName} - Historical Trendline and List</AnalyteTitle>
            </MuiBox>
            <MuiBox paddingLeft="5%" marginLeft="auto">
              <Svg set="assets" name={getImageByCategory(TestResultCategory.LAB)} size={70} />
            </MuiBox>
          </MuiBox>
        </MuiContainer>
      ) : null}
      <MuiBox my={3}>
        {renderChart ? (
          <MuiContainer
            data-testid="lab-trendline-graph"
            style={{ paddingBottom: Spacing.none }}
            maxWidth="xl"
          >
            <MuiBox component={MuiPaper} elevation={0} mb={2}>
              <LabTrendLineChart data={formattedData} labName={labName} />
            </MuiBox>
          </MuiContainer>
        ) : null}
        <Container
          style={{
            paddingBottom: Spacing.none,
            paddingTop: Spacing.none,
            paddingInline: Spacing.largeXLarge
          }}
          maxWidth="xl"
        >
          Your Results
        </Container>
        <Container maxWidth="xl" style={{ paddingInline: Spacing.largeXLarge }}>
          <MuiBox component={MuiPaper} elevation={0}>
            <MuiBox>
              <MuiTable data-testid="lab-historical-list" aria-label="Lab Historical Data">
                <colgroup>
                  <col style={{ width: '55%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
                <MuiTableHead>
                  <MuiTableRow>
                    <TableCell>Value</TableCell>
                    <TableCell>Interpretation</TableCell>
                    <TableCell>Reference Range</TableCell>
                    <TableCell>Date</TableCell>
                  </MuiTableRow>
                </MuiTableHead>
                <MuiTableBody>
                  {formattedData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item: LabHistoricalComponent) => (
                      <>
                        <MuiTableRow key={item.eventId}>
                          <TableCell>
                            {getLabValue(item.valueQuantity, oc(item).valueString(''))}
                          </TableCell>
                          <TableCell>{orNA(item.interpretation)}</TableCell>
                          <TableCell>{orNA(oc(item).referenceRange.text())}</TableCell>
                          <TableCell component="th" scope="row">
                            {dayjs(item.issued).format('MM/DD/YYYY')}
                          </TableCell>
                        </MuiTableRow>
                        <MuiTableRow>
                          <TableCell colSpan={5}>Comments: {orNA(item.notes)}</TableCell>
                        </MuiTableRow>
                        <MuiTableRow>
                          <TableCell colSpan={5}>
                            <Divider />
                          </TableCell>
                        </MuiTableRow>
                      </>
                    ))}
                </MuiTableBody>
              </MuiTable>
              <MuiTablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={formattedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </MuiBox>
          </MuiBox>
        </Container>
      </MuiBox>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  milleniumLoading: testResults.labHistoricalMillenniumLoadingSelector(state),
  milleniumError: testResults.labHistoricalMillenniumErrorSelector(state),
  milleniumData: testResults.labHistoricalMillenniumDataSelector(state),
  cdrData: testResults.labHistoricalCdrDataSelector(state),
  cdrLoading: testResults.labHistoricalCdrLoadingSelector(state),
  cdrError: testResults.labHistoricalCdrErrorSelector(state)
});

export default connect(mapStateToProps)(LabHistoricalScreen);
